import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Save, LogOut, Eye, ArrowUp, ArrowDown, ArrowLeft, Trash2, Plus, LayoutDashboard, Map, FileText, Package, Inbox, Users, Settings2, Image, Link2, ShieldCheck, LifeBuoy, FileSignature, Bell, Target, Box, Megaphone, BarChart3, PenLine, Twitter, HeartHandshake, MessageCircle, Fingerprint, Flag, Gamepad2, Gavel, Newspaper, FlaskConical } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { ICONS } from "@/components/iconMap";
import AdminDevlog from "@/components/admin/AdminDevlog";
import AdminSettings from "@/components/admin/AdminSettings";
import AdminFab from "@/components/admin/AdminFab";
import AdminFabPacks from "@/components/admin/AdminFabPacks";
import AdminOutreach from "@/components/admin/AdminOutreach";
import AdminTesting from "@/components/admin/AdminTesting";
import AdminPressroom from "@/components/admin/AdminPressroom";
import AdminInbox from "@/components/admin/AdminInbox";
import AdminTeam from "@/components/admin/AdminTeam";
import AdminSupport from "@/components/admin/AdminSupport";
import AdminHiring from "@/components/admin/AdminHiring";
import AdminContracts from "@/components/admin/AdminContracts";
import AdminSalesTeam from "@/components/admin/AdminSalesTeam";
import AdminAi3d from "@/components/admin/AdminAi3d";
import AdminCampaigns from "@/components/admin/AdminCampaigns";
import AdminCreators from "@/components/admin/AdminCreators";
import AdminJobPoster from "@/components/admin/AdminJobPoster";
import AdminSocialX from "@/components/admin/AdminSocialX";
import AdminNdisOps from "@/components/admin/AdminNdisOps";
import AdminDiscord from "@/components/admin/AdminDiscord";
import AdminNcDb from "@/components/admin/AdminNcDb";
import AdminAnalytics from "@/components/admin/AdminAnalytics";
import AdminNetworkReports from "@/components/admin/AdminNetworkReports";
import AdminAppeals from "@/components/admin/AdminAppeals";
import AdminSteamKit from "@/components/admin/AdminSteamKit";
import { RestrictedBanner, SessionCountdown } from "@/components/PortalChrome";

const iconBtnCls = "border border-white/10 p-1.5 text-zinc-400 hover:text-zinc-100 hover:border-white/30 transition-colors duration-300 disabled:opacity-30";

const inputCls = "w-full bg-[#050505] border border-white/10 px-3 py-2 font-mono text-sm text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-blue focus:border-nc-blue";
const labelCls = "font-mono text-[10px] tracking-[0.25em] text-zinc-500 uppercase block mb-1.5";

const MENU = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "analytics", label: "Analytics", icon: BarChart3 },
  { id: "roadmap", label: "Roadmap", icon: Map },
  { id: "content", label: "Content & Media", icon: Image },
  { id: "devlog", label: "Devlog Posts", icon: FileText },
  { id: "fab", label: "Fab Store", icon: Package },
  { id: "fabpacks", label: "Fab Pack QA", icon: Box },
  { id: "outreach", label: "Outreach", icon: Megaphone },
  { id: "testing", label: "Test Program", icon: FlaskConical },
  { id: "pressroom", label: "Newsroom", icon: Newspaper },
  { id: "inbox", label: "Inbox & Quotes", icon: Inbox },
  { id: "support", label: "Support Desk", icon: LifeBuoy },
  { id: "hiring", label: "Hiring & Payments", icon: FileSignature },
  { id: "contracts", label: "Contracts", icon: PenLine },
  { id: "salesteam", label: "Sales Team", icon: Target },
  { id: "campaigns", label: "Email Campaigns", icon: Megaphone },
  { id: "creators", label: "Creators", icon: Box },
  { id: "jobposter", label: "Job Poster", icon: Megaphone },
  { id: "socialx", label: "X Autoposter", icon: Twitter },
  { id: "ndisops", label: "NDIS Ops", icon: HeartHandshake },
  { id: "discord", label: "Discord", icon: MessageCircle },
  { id: "ncdb", label: "NC Database", icon: Fingerprint },
  { id: "netreview", label: "Network Reviews", icon: Flag },
  { id: "appeals", label: "Ban Appeals", icon: Gavel },
  { id: "steamkit", label: "Steam Page Kit", icon: Gamepad2 },
  { id: "ai3d", label: "AI 3D Lab", icon: Box },
  { id: "team", label: "Team & Accounts", icon: Users },
  { id: "settings", label: "Settings", icon: Settings2 },
];

function NotificationBell() {
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const load = () => api.get("/notifications").then((r) => setData(r.data)).catch(() => {});
    load();
    const id = setInterval(load, 10000);
    return () => clearInterval(id);
  }, []);

  const toggle = async () => {
    setOpen((o) => !o);
    if (!open && data.unread > 0) {
      await api.post("/notifications/read").catch(() => {});
      setData((d) => ({ ...d, unread: 0 }));
    }
  };

  return (
    <div className="relative">
      <button data-testid="admin-notifications-bell" onClick={toggle} className="relative border border-white/10 p-2 text-zinc-400 hover:text-zinc-100 hover:border-white/30 transition-colors">
        <Bell className="w-4 h-4" />
        {data.unread > 0 && (
          <span data-testid="admin-notifications-badge" className="absolute -top-1.5 -right-1.5 min-w-4 h-4 px-1 bg-nc-orange text-white font-mono text-[9px] flex items-center justify-center">{data.unread}</span>
        )}
      </button>
      {open && (
        <div data-testid="admin-notifications-panel" className="absolute right-0 top-11 z-50 w-[380px] border border-white/15 bg-[#0a0a0a] shadow-2xl max-h-[420px] overflow-y-auto">
          <div className="border-b border-white/10 px-4 py-2.5 font-mono text-[9px] tracking-[0.3em] text-nc-orange uppercase">Notifications</div>
          {data.items.length === 0 && <p className="font-mono text-[10px] text-zinc-600 p-4">No notifications.</p>}
          {data.items.map((n, i) => (
            <button
              key={n.id}
              data-testid={`notification-${i + 1}`}
              onClick={() => { setOpen(false); if (n.link) navigate(n.link); }}
              className="w-full text-left px-4 py-3 border-b border-white/5 hover:bg-white/5 transition-colors"
            >
              <p className="font-mono text-[10px] text-zinc-100 leading-snug">{n.title}</p>
              <p className="font-mono text-[9px] text-zinc-500 mt-1 leading-snug">{n.detail}</p>
              <p className="font-mono text-[8px] text-zinc-600 mt-1 uppercase tracking-[0.2em]">{String(n.at).slice(5, 16).replace("T", " ")} UTC</p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function LiveAuditFeed() {
  const [events, setEvents] = useState([]);
  useEffect(() => {
    const load = () => api.get("/audit").then((r) => setEvents(r.data.slice(0, 12))).catch(() => {});
    load();
    const id = setInterval(load, 8000);
    return () => clearInterval(id);
  }, []);
  return (
    <div data-testid="live-audit-feed" className="border border-white/10 bg-black/40">
      <div className="border-b border-white/10 px-5 py-3 flex items-center justify-between">
        <span className="font-mono text-[10px] tracking-[0.3em] text-nc-orange uppercase flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" /> Live Portal Activity
        </span>
        <span className="font-mono text-[9px] text-zinc-600 uppercase">auto-refresh 8s</span>
      </div>
      <div className="px-5 py-2 max-h-[300px] overflow-y-auto">
        {events.length === 0 && <p className="font-mono text-[10px] text-zinc-600 py-4">No portal activity recorded yet.</p>}
        {events.map((e) => (
          <div key={e.id} className="flex items-start gap-3 border-b border-white/5 py-2 font-mono text-[10px]">
            <span className="text-zinc-600 shrink-0 w-[110px]">{String(e.at).slice(5, 19).replace("T", " ")}</span>
            <span className={`shrink-0 uppercase ${e.action.includes("failed") || e.action.includes("ban") || e.action.includes("delete") ? "text-red-400" : e.action.includes("login") || e.action.includes("unban") ? "text-nc-green" : "text-nc-blue"}`}>{e.action}</span>
            <span className="text-zinc-400 truncate">{e.actor_email || "—"}</span>
            <span className="text-zinc-600 truncate">{e.detail}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminPage() {
  const navigate = useNavigate();
  const [roadmap, setRoadmap] = useState(null);
  const [saving, setSaving] = useState(false);
  const [section, setSection] = useState("overview");

  // deep-link: /admin?section=contracts opens a tab directly (used by notifications)
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const s = searchParams.get("section");
    if (s === "investors") setSection("outreach");
    else if (s && MENU.some((t) => t.id === s)) setSection(s);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    api.get("/auth/me")
      .then((res) => {
        if (res.data.role !== "admin") {
          navigate("/");
          return null;
        }
        return api.get("/roadmap");
      })
      .then((r) => { if (r) setRoadmap(r.data); })
      .catch(() => {
        localStorage.removeItem("nc_token");
        navigate("/admin/login");
      });
  }, [navigate]);

  const mutate = (fn) => setRoadmap((r) => { const c = structuredClone(r); fn(c); return c; });
  const updatePhase = (pi, field, value) => mutate((c) => { c.phases[pi][field] = value; });
  const updateItem = (pi, ii, field, value) => mutate((c) => { c.phases[pi].items[ii][field] = value; });
  const updateGate = (gi, field, value) => mutate((c) => { c.gate[gi][field] = value; });

  const renumber = (c) => { c.phases.forEach((p, i) => { p.number = String(i + 1).padStart(2, "0"); }); };
  const addPhase = () => mutate((c) => {
    c.phases.push({ id: crypto.randomUUID(), number: "", title: "New Phase", tagline: "", description: "", targetDate: "", accent: c.phases.length % 2 === 0 ? "blue" : "orange", items: [] });
    renumber(c);
  });
  const removePhase = (pi) => mutate((c) => { c.phases.splice(pi, 1); renumber(c); });
  const movePhase = (pi, dir) => mutate((c) => {
    const j = pi + dir;
    if (j < 0 || j >= c.phases.length) return;
    [c.phases[pi], c.phases[j]] = [c.phases[j], c.phases[pi]];
    renumber(c);
  });
  const addTask = (pi) => mutate((c) => {
    c.phases[pi].items.push({ id: crypto.randomUUID(), icon: "Terminal", title: "New Task", description: "", status: "todo", progress: 0 });
  });
  const removeTask = (pi, ii) => mutate((c) => { c.phases[pi].items.splice(ii, 1); });
  const moveTask = (pi, ii, dir) => mutate((c) => {
    const items = c.phases[pi].items;
    const j = ii + dir;
    if (j < 0 || j >= items.length) return;
    [items[ii], items[j]] = [items[j], items[ii]];
  });
  const addLink = () => mutate((c) => {
    if (!c.links) c.links = [];
    c.links.push({ id: crypto.randomUUID(), label: "NEW LINK", url: "", icon: "Globe" });
  });
  const updateLink = (li, field, value) => mutate((c) => { c.links[li][field] = value; });
  const removeLink = (li) => mutate((c) => { c.links.splice(li, 1); });
  const addMedia = () => mutate((c) => {
    if (!c.media) c.media = [];
    c.media.push({ id: crypto.randomUUID(), type: "image", url: "", caption: "" });
  });
  const updateMedia = (mi, field, value) => mutate((c) => { c.media[mi][field] = value; });
  const removeMedia = (mi) => mutate((c) => { c.media.splice(mi, 1); });

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put("/roadmap", roadmap);
      toast.success("Roadmap updated successfully");
    } catch {
      toast.error("Failed to save roadmap");
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("nc_token");
    navigate("/admin/login");
  };

  if (!roadmap) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <p data-testid="admin-loading" className="font-mono text-sm tracking-[0.3em] text-zinc-500 uppercase animate-pulse">Loading editor...</p>
      </div>
    );
  }

  return (
    <div data-testid="admin-page" className="min-h-screen bg-[#050505] text-zinc-100 flex flex-col">
      <RestrictedBanner area="ADMIN" />
      <div className="flex flex-1">
      {/* Sidebar */}
      <aside data-testid="admin-sidebar" className="w-60 shrink-0 border-r border-white/10 bg-[#070707] sticky top-0 h-screen flex flex-col">
        <div className="px-5 py-5 border-b border-white/10">
          <p className="font-sans font-bold uppercase text-sm tracking-wide text-zinc-50">Specpixel Admin</p>
          <p className="font-mono text-[9px] tracking-[0.3em] text-zinc-600 uppercase mt-1">Control Room</p>
        </div>
        <nav className="flex-1 overflow-y-auto py-3 flex flex-col gap-0.5 px-2">
          {MENU.map((m) => {
            const Icon = m.icon;
            const active = section === m.id;
            return (
              <button
                key={m.id}
                data-testid={`admin-menu-${m.id}`}
                onClick={() => setSection(m.id)}
                className={`flex items-center gap-3 px-3 py-2.5 font-mono text-[11px] tracking-[0.15em] uppercase text-left transition-colors duration-200 ${
                  active ? "bg-nc-blue/10 text-nc-blue border-l-2 border-nc-blue" : "text-zinc-400 hover:text-zinc-100 hover:bg-white/5 border-l-2 border-transparent"
                }`}
              >
                <Icon className="w-4 h-4 shrink-0" /> {m.label}
              </button>
            );
          })}
        </nav>
        <div className="border-t border-white/10 p-3 flex flex-col gap-2">
          <Link to="/" data-testid="admin-view-site-link" className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-400 hover:text-zinc-100 border border-white/10 px-3 py-2 flex items-center gap-2 transition-colors duration-300">
            <Eye className="w-3.5 h-3.5" /> View Site
          </Link>
          <Link to="/dev-hub" data-testid="admin-devhub-link" className="font-mono text-[10px] tracking-[0.2em] uppercase text-nc-orange border border-nc-orange/30 hover:bg-nc-orange/10 px-3 py-2 flex items-center gap-2 transition-colors duration-300">
            <ShieldCheck className="w-3.5 h-3.5" /> Dev Hub
          </Link>
          <button
            data-testid="admin-logout-button"
            onClick={handleLogout}
            className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-400 hover:text-red-400 border border-white/10 px-3 py-2 flex items-center gap-2 transition-colors duration-300"
          >
            <LogOut className="w-3.5 h-3.5" /> Logout
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="border-b border-white/10 sticky top-0 bg-[#050505]/90 backdrop-blur-md z-10">
          <div className="px-6 py-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <button
                data-testid="admin-back-btn"
                onClick={() => (window.history.state?.idx > 0 ? navigate(-1) : navigate("/"))}
                className="shrink-0 flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-300 hover:text-nc-blue border border-white/15 hover:border-nc-blue/50 px-2.5 py-1.5 transition-colors duration-300"
              >
                <ArrowLeft className="w-3.5 h-3.5" /> Back
              </button>
              <div className="min-w-0">
                <h1 data-testid="admin-section-title" className="font-sans font-bold uppercase text-lg tracking-wide">{MENU.find((m) => m.id === section)?.label}</h1>
                <p className="font-mono text-[10px] tracking-[0.25em] text-zinc-500 uppercase">New Citizen // Admin</p>
              </div>
            </div>
            <div className="hidden md:flex items-center gap-3">
              <Link to="/" data-testid="admin-view-site" className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-400 hover:text-nc-blue border border-white/15 hover:border-nc-blue/50 px-2.5 py-1.5 transition-colors duration-300">Main Site</Link>
              <SessionCountdown />
              <NotificationBell />
              <div data-testid="admin-clearance" className="border border-nc-orange/40 bg-nc-orange/5 px-3 py-1.5 text-right">
                <p className="font-mono text-[8px] tracking-[0.3em] text-nc-orange uppercase">Clearance: Level 5 // Admin</p>
                <p className="font-mono text-[8px] tracking-[0.2em] text-zinc-500 uppercase">All actions are logged</p>
              </div>
            </div>
            {(section === "roadmap" || section === "content" || section === "overview") && (
              <button
                data-testid="admin-save-button"
                onClick={handleSave}
                disabled={saving}
                className="font-sans font-semibold text-sm uppercase tracking-[0.15em] bg-nc-blue hover:bg-nc-blue/80 disabled:opacity-50 text-white px-5 py-2 flex items-center gap-2 transition-colors duration-300"
              >
                <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save"}
              </button>
            )}
          </div>
        </header>

        <main className="px-6 py-8 flex flex-col gap-8 max-w-6xl w-full mx-auto">
        {section === "analytics" && <AdminAnalytics />}
        {section === "overview" && (
          <section data-testid="admin-overview" className="flex flex-col gap-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { label: "Phases", value: roadmap.phases.length, hint: `${roadmap.phases.reduce((n, p) => n + p.items.filter((i) => i.status === "done").length, 0)} / ${roadmap.phases.reduce((n, p) => n + p.items.length, 0)} tasks done` },
                { label: "Roadmap Progress", value: `${Math.round(roadmap.phases.reduce((n, p) => n + p.items.reduce((a, i) => a + i.progress, 0), 0) / Math.max(roadmap.phases.reduce((n, p) => n + p.items.length, 0), 1))}%`, hint: "Average task progress" },
                { label: "Gate Checks", value: `${roadmap.gate.filter((g) => g.status === "done").length} / ${roadmap.gate.length}`, hint: "Pre-launch cleared" },
              ].map((s) => (
                <div key={s.label} className="border border-white/10 bg-black/40 p-5">
                  <p className="font-mono text-[9px] tracking-[0.3em] text-zinc-500 uppercase">{s.label}</p>
                  <p className="font-sans font-bold text-3xl text-zinc-50 mt-2">{s.value}</p>
                  <p className="font-mono text-[10px] text-zinc-500 mt-1">{s.hint}</p>
                </div>
              ))}
            </div>
            <div className="border border-nc-blue/30 bg-nc-blue/5 p-6">
              <p className="font-mono text-[10px] tracking-[0.3em] text-nc-blue uppercase mb-3">Quick workflow</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 font-mono text-xs text-zinc-400 leading-relaxed">
                <p>01 — Update task statuses under <button className="text-nc-blue underline" onClick={() => setSection("roadmap")}>Roadmap</button>, hit Save</p>
                <p>02 — Publish progress posts under <button className="text-nc-blue underline" onClick={() => setSection("devlog")}>Devlog Posts</button></p>
                <p>03 — Answer clients under <button className="text-nc-blue underline" onClick={() => setSection("inbox")}>Inbox & Quotes</button></p>
                <p>04 — Manage devs & bans under <button className="text-nc-blue underline" onClick={() => setSection("team")}>Team & Accounts</button></p>
              </div>
            </div>
            <LiveAuditFeed />
          </section>
        )}

        {section === "roadmap" && (<>
        <section className="border border-white/10 bg-black/40 p-6">
          <h2 className="font-sans font-semibold uppercase tracking-widest text-base text-nc-blue mb-5">Hero / Branding</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[["studio", "Studio"], ["game", "Game Title"], ["gameTagline", "Game Tagline"], ["version", "Version"], ["title", "Roadmap Title"], ["motto", "Motto"], ["footer", "Footer Motto"], ["announcement", "Announcement Strip"]].map(([field, label]) => (
              <div key={field}>
                <label className={labelCls}>{label}</label>
                <input
                  data-testid={`admin-hero-${field}-input`}
                  className={inputCls}
                  value={roadmap[field]}
                  onChange={(e) => mutate((c) => { c[field] = e.target.value; })}
                />
              </div>
            ))}
          </div>
        </section>
        </>)}

        {section === "roadmap" && (<>
        {roadmap.phases.map((phase, pi) => (
          <section key={phase.id} data-testid={`admin-phase-${pi + 1}`} className="border border-white/10 bg-black/40">
            <div className="border-b border-white/10 px-6 py-4 flex items-center justify-between gap-3">
              <h2 className={`font-sans font-semibold uppercase tracking-widest text-base ${phase.accent === "blue" ? "text-nc-blue" : "text-nc-orange"}`}>
                Phase {phase.number} — {phase.title}
              </h2>
              <div className="flex items-center gap-2 shrink-0">
                <button data-testid={`admin-phase-${pi + 1}-move-up-button`} onClick={() => movePhase(pi, -1)} disabled={pi === 0} className={iconBtnCls} title="Move phase up">
                  <ArrowUp className="w-3.5 h-3.5" />
                </button>
                <button data-testid={`admin-phase-${pi + 1}-move-down-button`} onClick={() => movePhase(pi, 1)} disabled={pi === roadmap.phases.length - 1} className={iconBtnCls} title="Move phase down">
                  <ArrowDown className="w-3.5 h-3.5" />
                </button>
                <button data-testid={`admin-phase-${pi + 1}-delete-button`} onClick={() => removePhase(pi)} className={`${iconBtnCls} hover:text-red-400`} title="Delete phase">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
            <div className="p-6 flex flex-col gap-6">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className={labelCls}>Phase Title</label>
                  <input data-testid={`admin-phase-${pi + 1}-title-input`} className={inputCls} value={phase.title} onChange={(e) => updatePhase(pi, "title", e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>Tagline</label>
                  <input data-testid={`admin-phase-${pi + 1}-tagline-input`} className={inputCls} value={phase.tagline} onChange={(e) => updatePhase(pi, "tagline", e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>Description</label>
                  <input data-testid={`admin-phase-${pi + 1}-description-input`} className={inputCls} value={phase.description} onChange={(e) => updatePhase(pi, "description", e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>Target Date</label>
                  <input data-testid={`admin-phase-${pi + 1}-target-date-input`} className={inputCls} value={phase.targetDate || ""} onChange={(e) => updatePhase(pi, "targetDate", e.target.value)} placeholder="e.g. Q4 2026" />
                </div>
                <div>
                  <label className={labelCls}>Accent Color</label>
                  <select data-testid={`admin-phase-${pi + 1}-accent-select`} className={inputCls} value={phase.accent} onChange={(e) => updatePhase(pi, "accent", e.target.value)}>
                    <option value="blue">Blue</option>
                    <option value="orange">Orange</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {phase.items.map((item, ii) => (
                  <div key={item.id} data-testid={`admin-phase-${pi + 1}-item-${ii + 1}`} className="border border-white/10 p-4 flex flex-col gap-3 bg-[#050505]/50">
                    <div className="flex items-center justify-end gap-2">
                      <button data-testid={`admin-phase-${pi + 1}-item-${ii + 1}-move-up-button`} onClick={() => moveTask(pi, ii, -1)} disabled={ii === 0} className={iconBtnCls} title="Move task up">
                        <ArrowUp className="w-3.5 h-3.5" />
                      </button>
                      <button data-testid={`admin-phase-${pi + 1}-item-${ii + 1}-move-down-button`} onClick={() => moveTask(pi, ii, 1)} disabled={ii === phase.items.length - 1} className={iconBtnCls} title="Move task down">
                        <ArrowDown className="w-3.5 h-3.5" />
                      </button>
                      <button data-testid={`admin-phase-${pi + 1}-item-${ii + 1}-delete-button`} onClick={() => removeTask(pi, ii)} className={`${iconBtnCls} hover:text-red-400`} title="Delete task">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className={labelCls}>Task Title</label>
                        <input data-testid={`admin-phase-${pi + 1}-item-${ii + 1}-title-input`} className={inputCls} value={item.title} onChange={(e) => updateItem(pi, ii, "title", e.target.value)} />
                      </div>
                      <div>
                        <label className={labelCls}>Icon</label>
                        <select
                          data-testid={`admin-phase-${pi + 1}-item-${ii + 1}-icon-select`}
                          className={inputCls}
                          value={item.icon}
                          onChange={(e) => updateItem(pi, ii, "icon", e.target.value)}
                        >
                          {Object.keys(ICONS).map((k) => <option key={k} value={k}>{k}</option>)}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className={labelCls}>Description</label>
                      <textarea
                        data-testid={`admin-phase-${pi + 1}-item-${ii + 1}-description-input`}
                        className={`${inputCls} resize-none`}
                        rows={2}
                        value={item.description}
                        onChange={(e) => updateItem(pi, ii, "description", e.target.value)}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3 items-end">
                      <div>
                        <label className={labelCls}>Status</label>
                        <select
                          data-testid={`admin-phase-${pi + 1}-item-${ii + 1}-status-select`}
                          className={inputCls}
                          value={item.status}
                          onChange={(e) => updateItem(pi, ii, "status", e.target.value)}
                        >
                          <option value="todo">TODO</option>
                          <option value="in-progress">IN PROGRESS</option>
                          <option value="done">DONE</option>
                        </select>
                      </div>
                      <div>
                        <label className={labelCls}>Progress — {item.progress}%</label>
                        <input
                          data-testid={`admin-phase-${pi + 1}-item-${ii + 1}-progress-input`}
                          type="range"
                          min={0}
                          max={100}
                          step={5}
                          value={item.progress}
                          onChange={(e) => updateItem(pi, ii, "progress", Number(e.target.value))}
                          className="w-full accent-[#2E6BFF]"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <button data-testid={`admin-phase-${pi + 1}-add-task-button`} onClick={() => addTask(pi)} className="self-start font-mono text-[10px] tracking-[0.2em] uppercase text-nc-blue border border-nc-blue/40 hover:bg-nc-blue/10 px-4 py-2 flex items-center gap-2 transition-colors duration-300">
                <Plus className="w-3.5 h-3.5" /> Add Task
              </button>
            </div>
          </section>
        ))}

        <button data-testid="admin-add-phase-button" onClick={addPhase} className="self-center font-sans font-semibold text-sm uppercase tracking-[0.15em] text-zinc-100 border border-white/20 hover:border-nc-blue hover:text-nc-blue px-6 py-3 flex items-center gap-2 transition-colors duration-300">
          <Plus className="w-4 h-4" /> Add Phase
        </button>

        <section className="border border-white/10 bg-black/40">
          <div className="border-b border-white/10 px-6 py-4">
            <h2 className="font-sans font-semibold uppercase tracking-widest text-base text-zinc-100">Pre-Launch Gate</h2>
          </div>
          <div className="p-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
            {roadmap.gate.map((item, gi) => (
              <div key={item.id} data-testid={`admin-gate-${gi + 1}`} className="border border-white/10 p-4 flex flex-col gap-3 bg-[#050505]/50">
                <div>
                  <label className={labelCls}>Title</label>
                  <input data-testid={`admin-gate-${gi + 1}-title-input`} className={inputCls} value={item.title} onChange={(e) => updateGate(gi, "title", e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>Description</label>
                  <textarea data-testid={`admin-gate-${gi + 1}-description-input`} className={`${inputCls} resize-none`} rows={2} value={item.description} onChange={(e) => updateGate(gi, "description", e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>Status</label>
                  <select data-testid={`admin-gate-${gi + 1}-status-select`} className={inputCls} value={item.status} onChange={(e) => updateGate(gi, "status", e.target.value)}>
                    <option value="todo">TODO</option>
                    <option value="in-progress">IN PROGRESS</option>
                    <option value="done">DONE</option>
                  </select>
                </div>
              </div>
            ))}
          </div>
        </section>
        </>)}

        {section === "content" && (<>
        <section className="border border-white/10 bg-black/40 p-6">
          <h2 className="font-sans font-semibold uppercase tracking-widest text-base text-nc-orange mb-5">Community Links</h2>
          <div className="flex flex-col gap-3">
            {(roadmap.links || []).map((link, li) => (
              <div key={link.id} data-testid={`admin-link-${li + 1}`} className="grid grid-cols-1 sm:grid-cols-[1fr_2fr_180px_auto] gap-3 items-end border border-white/10 p-4 bg-[#050505]/50">
                <div>
                  <label className={labelCls}>Label</label>
                  <input data-testid={`admin-link-${li + 1}-label-input`} className={inputCls} value={link.label} onChange={(e) => updateLink(li, "label", e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>URL</label>
                  <input data-testid={`admin-link-${li + 1}-url-input`} className={inputCls} value={link.url} onChange={(e) => updateLink(li, "url", e.target.value)} placeholder="https://..." />
                </div>
                <div>
                  <label className={labelCls}>Icon</label>
                  <select data-testid={`admin-link-${li + 1}-icon-select`} className={inputCls} value={link.icon} onChange={(e) => updateLink(li, "icon", e.target.value)}>
                    {Object.keys(ICONS).map((k) => <option key={k} value={k}>{k}</option>)}
                  </select>
                </div>
                <button data-testid={`admin-link-${li + 1}-delete-button`} onClick={() => removeLink(li)} className={`${iconBtnCls} hover:text-red-400`} title="Delete link">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
            <button data-testid="admin-add-link-button" onClick={addLink} className="self-start font-mono text-[10px] tracking-[0.2em] uppercase text-nc-orange border border-nc-orange/40 hover:bg-nc-orange/10 px-4 py-2 flex items-center gap-2 transition-colors duration-300">
              <Plus className="w-3.5 h-3.5" /> Add Link
            </button>
          </div>
        </section>

        <section className="border border-white/10 bg-black/40 p-6">
          <h2 className="font-sans font-semibold uppercase tracking-widest text-base text-nc-blue mb-5">Media Gallery</h2>
          <div className="flex flex-col gap-3">
            {(roadmap.media || []).map((item, mi) => (
              <div key={item.id} data-testid={`admin-media-${mi + 1}`} className="grid grid-cols-1 sm:grid-cols-[140px_2fr_1fr_auto] gap-3 items-end border border-white/10 p-4 bg-[#050505]/50">
                <div>
                  <label className={labelCls}>Type</label>
                  <select data-testid={`admin-media-${mi + 1}-type-select`} className={inputCls} value={item.type} onChange={(e) => updateMedia(mi, "type", e.target.value)}>
                    <option value="image">Image</option>
                    <option value="youtube">YouTube</option>
                    <option value="x">X Video</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls}>URL</label>
                  <input data-testid={`admin-media-${mi + 1}-url-input`} className={inputCls} value={item.url} onChange={(e) => updateMedia(mi, "url", e.target.value)} placeholder="https://... or /assets/..." />
                </div>
                <div>
                  <label className={labelCls}>Caption</label>
                  <input data-testid={`admin-media-${mi + 1}-caption-input`} className={inputCls} value={item.caption} onChange={(e) => updateMedia(mi, "caption", e.target.value)} />
                </div>
                <button data-testid={`admin-media-${mi + 1}-delete-button`} onClick={() => removeMedia(mi)} className={`${iconBtnCls} hover:text-red-400`} title="Delete media">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
            <button data-testid="admin-add-media-button" onClick={addMedia} className="self-start font-mono text-[10px] tracking-[0.2em] uppercase text-nc-blue border border-nc-blue/40 hover:bg-nc-blue/10 px-4 py-2 flex items-center gap-2 transition-colors duration-300">
              <Plus className="w-3.5 h-3.5" /> Add Media
            </button>
          </div>
        </section>
        </>)}

        {section === "devlog" && <AdminDevlog />}

        {section === "fab" && <AdminFab />}
        {section === "fabpacks" && <AdminFabPacks />}
        {section === "outreach" && <AdminOutreach />}
        {section === "testing" && <AdminTesting />}
        {section === "pressroom" && <AdminPressroom />}

        {section === "inbox" && <AdminInbox />}

        {section === "support" && <AdminSupport />}

        {section === "hiring" && <AdminHiring />}
        {section === "contracts" && <AdminContracts />}
        {section === "salesteam" && <AdminSalesTeam />}
        {section === "campaigns" && <AdminCampaigns />}
        {section === "creators" && <AdminCreators />}
        {section === "jobposter" && <AdminJobPoster />}
        {section === "socialx" && <AdminSocialX />}
        {section === "ndisops" && <AdminNdisOps />}
        {section === "discord" && <AdminDiscord />}
        {section === "ncdb" && <AdminNcDb />}
        {section === "netreview" && <AdminNetworkReports />}
        {section === "appeals" && <AdminAppeals />}
        {section === "steamkit" && <AdminSteamKit />}
        {section === "ai3d" && <AdminAi3d />}

        {section === "team" && <AdminTeam />}

        {section === "settings" && <AdminSettings />}
      </main>
      </div>
      </div>
    </div>
  );
}
